// EXPERIMENT: sign_form_signer_carousel_colors_experiment — remove with experiment cleanup
import type React from 'react';

import { colors } from '@change/ds-tokens';

import type { SignerCarouselColorScheme } from '#src/app/pages/petitionDetails/shared/hooks';

// Sunrise primitive palette. The DS Tailwind theme exposes only semantic color utilities, so the
// primitives are read from the token JS and published as CSS custom properties on `ColoredSignerPill`.
const { Blue, Purple, Yellow, Base, Gray } = colors.primitive;

/** The sign sheet's light-mode text color (DS `typography-strong`), pinned so v1 does not depend on inheritance. */
const LIGHT_TEXT = Gray['Gray Slate'][1000];

type SignerPillStyle = React.CSSProperties & Readonly<Record<`--${string}`, string>>;

type SignerPillPalette = Readonly<{
	/** Pill background. */
	fill: string;
	/** Name and metatext color. */
	text: string;
}>;

export type SignerPillColors = SignerPillPalette &
	Readonly<{
		/** Used under the `.dark` class, since primitives do not flip with the theme. */
		dark: SignerPillPalette;
		/** Both palettes as the custom properties `ColoredSignerPill` reads, built once per entry. */
		style: SignerPillStyle;
	}>;

export type ColoredSignerCarouselScheme = Exclude<SignerCarouselColorScheme, 'default'>;

function pillColors(light: SignerPillPalette, dark: SignerPillPalette): SignerPillColors {
	return {
		...light,
		dark,
		style: {
			'--signer-pill-fill': light.fill,
			'--signer-pill-text': light.text,
			'--signer-pill-fill-dark': dark.fill,
			'--signer-pill-text-dark': dark.text,
		},
	};
}

// Every text/fill pair meets WCAG AA (4.5:1) in both modes; the scheme tests assert it.
const COLOR_SCHEMES: Readonly<
	Record<ColoredSignerCarouselScheme, readonly [SignerPillColors, ...(readonly SignerPillColors[])]>
> = {
	v1: [
		pillColors({ fill: Blue[100], text: LIGHT_TEXT }, { fill: Blue[800], text: Base.White }),
		pillColors({ fill: Purple[100], text: LIGHT_TEXT }, { fill: Purple[800], text: Base.White }),
		pillColors({ fill: Yellow[100], text: LIGHT_TEXT }, { fill: Yellow[800], text: Base.White }),
	],
	v2: [pillColors({ fill: Blue[100], text: Blue[700] }, { fill: Blue[200], text: Blue[800] })],
	// The same in both modes: it already reads on the dark sheet, and stays darker than v2 in each.
	v3: [pillColors({ fill: Blue[700], text: Base.White }, { fill: Blue[700], text: Base.White })],
};

/**
 * Picks the pill colors for a signer. Multi-color schemes cycle by the signer's index in the base (not
 * doubled) list, so a signer keeps the same color in both copies of the scrolling loop.
 */
export function pickSignerPillColors(scheme: ColoredSignerCarouselScheme, baseIndex: number): SignerPillColors {
	const entries = COLOR_SCHEMES[scheme];
	return entries[baseIndex % entries.length] ?? entries[0];
}
